import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  updateHouseholdAPI,
  prepareMemberData,
  validateMemberData,
} from "../../api/updateHouseholdAPI";
import {
  formatDateForDisplay,
  formatDateForInput,
} from "../../utils/formatters";
import { validateDob, getTodayYmdLocal } from "../../utils/dateFieldConstraints";
import ClientPortalLayout from "../ClientPortalLayout";
import EditControls from "./shared/EditControls";
import clientAxiosInstance from "../../api/clientAxiosInstance";
import { useConfirm } from "../../context/ConfirmContext";

const FinalReviewV2 = ({
  householdData,
  household_id,
  clientToken,
  onLogout,
  onDataUpdate,
}) => {
  const navigate = useNavigate();
  const { household_id: householdIdFromRoute } = useParams();
  const actualHouseholdId = household_id || householdIdFromRoute;
  const confirmDialog = useConfirm();

  const [expandedOwners, setExpandedOwners] = useState(new Set());
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [clearingAdvisorNote, setClearingAdvisorNote] = useState(false);

  const handleClearAdvisorNote = useCallback(async () => {
    if (!actualHouseholdId || !clientToken || clearingAdvisorNote) return;
    setClearingAdvisorNote(true);
    try {
      await clientAxiosInstance.delete(
        `/client/household/${actualHouseholdId}/advisor-note`,
        { headers: { Authorization: `Bearer ${clientToken}` } },
      );
      if (onDataUpdate) onDataUpdate();
    } catch (e) {
      console.error("Failed to clear advisor note:", e);
    } finally {
      setClearingAdvisorNote(false);
    }
  }, [actualHouseholdId, clientToken, clearingAdvisorNote, onDataUpdate]);

  const confirmClearAdvisorNote = useCallback(async () => {
    if (
      !householdData?.advisor_note ||
      householdData.advisor_note.trim() === ""
    )
      return;
    const ok = await confirmDialog({
      title: "Delete advisor note?",
      message: "This note will be removed and cannot be shown again.",
      confirmLabel: "Delete",
    });
    if (ok) handleClearAdvisorNote();
  }, [householdData?.advisor_note, handleClearAdvisorNote, confirmDialog]);

  useEffect(() => {
    if (householdData) {
      setEditedData(JSON.parse(JSON.stringify(householdData)));
    }
  }, [householdData]);

  if (!householdData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500 mx-auto" />
          <p className="mt-4 text-xl text-gray-600">Loading your information</p>
        </div>
      </div>
    );
  }

  const getAccountOwners = () => {
    const ownersMap = new Map();
    const dataToUse = isEditing && editedData ? editedData : householdData;
    const accounts = dataToUse?.accounts || [];
    const members = dataToUse?.members || [];

    accounts.forEach((account) => {
      (account.owners_info || []).forEach((ownerInfo) => {
        const memberId = ownerInfo.id;
        if (!ownersMap.has(memberId)) {
          const member = members.find((m) => m.id === memberId);
          if (member) {
            ownersMap.set(memberId, { memberId, member, accounts: [] });
          }
        }
        if (ownersMap.has(memberId)) {
          ownersMap.get(memberId).accounts.push({
            accountId: account.id,
            accountType: account.type,
            accountNumber:
              account.new_account_number ||
              account.transfer_info?.old_account_number,
            share: ownerInfo.share,
            type: ownerInfo.type,
          });
        }
      });
    });

    return Array.from(ownersMap.values());
  };

  const accountOwners = getAccountOwners();

  const toggleOwner = (memberId) => {
    setExpandedOwners((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  const getPersonalInfo = (member) => {
    let pi = member.personal_info;
    if (typeof pi === "string") {
      try {
        pi = JSON.parse(pi);
      } catch {
        return null;
      }
    }
    return pi;
  };

  const getAddress = (personalInfo) => {
    if (!personalInfo?.address) return null;
    const addresses = Array.isArray(personalInfo.address)
      ? personalInfo.address
      : [personalInfo.address];
    return addresses.find((a) => a.type === "home") || addresses[0] || null;
  };

  const maskSSN = (ssn) => {
    if (!ssn) return "N/A";
    const digits = String(ssn).replace(/\D/g, "");
    if (digits.length < 4) return "N/A";
    return `***-**-${digits.slice(-4)}`;
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError("");
    setSuccess("");
  };
  const handleCancel = () => {
    setIsEditing(false);
    setEditedData(JSON.parse(JSON.stringify(householdData)));
    setValidationErrors({});
    setError("");
    setSuccess("");
  };

  const handleInputChange = (memberId, fieldPath, value) => {
    const newData = { ...editedData };
    const members = [...(newData.members || [])];
    const memberIndex = members.findIndex((m) => m.id === memberId);
    if (memberIndex === -1) return;

    const member = { ...members[memberIndex] };
    let personalInfo = getPersonalInfo(member);
    if (!personalInfo) personalInfo = {};
    else personalInfo = { ...personalInfo };

    const pathParts = fieldPath.split(".");
    if (pathParts.length === 1) {
      personalInfo[fieldPath] = value;
    } else if (pathParts.length === 2) {
      const [parent, child] = pathParts;
      if (parent === "address") {
        let addresses = personalInfo.address || [];
        if (!Array.isArray(addresses)) addresses = [addresses];
        addresses = [...addresses];
        const idx = addresses.findIndex((a) => a.type === "home");
        const addrIdx = idx >= 0 ? idx : 0;
        if (addrIdx >= 0 && addresses[addrIdx]) {
          addresses[addrIdx] = { ...addresses[addrIdx], [child]: value };
        } else {
          addresses[0] = { type: "home", [child]: value };
        }
        personalInfo.address = addresses;
      } else {
        personalInfo[parent] = {
          ...(personalInfo[parent] || {}),
          [child]: value,
        };
      }
    }

    member.personal_info = personalInfo;
    members[memberIndex] = member;
    newData.members = members;
    setEditedData(newData);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const dobErrors = {};
      for (const ownerData of accountOwners) {
        const member = editedData.members?.find(
          (m) => m.id === ownerData.memberId,
        );
        if (!member) continue;
        const personalInfo = getPersonalInfo(member);
        const dobError = validateDob(personalInfo?.dob);
        if (dobError) dobErrors[`${ownerData.memberId}_dob`] = dobError;
      }
      setValidationErrors(dobErrors);
      if (Object.keys(dobErrors).length > 0) {
        setError("Please correct the highlighted fields before saving.");
        return;
      }

      for (const ownerData of accountOwners) {
        const member = editedData.members?.find(
          (m) => m.id === ownerData.memberId,
        );
        if (member) {
          const errors = validateMemberData(
            member,
            (editedData || householdData)?.accounts || [],
          );
          if (errors.length > 0) {
            setError(
              `Validation error for ${ownerData.member.personal_info?.first_name || "member"}: ${errors.join(". ")}`,
            );
            return;
          }
        }
      }

      const membersPayload = prepareMemberData(
        editedData.members || [],
        householdData.members || [],
      );
      const payload = { action: "final_review" };
      if (Object.keys(membersPayload).length > 0) {
        payload.household_members = membersPayload;
      }

      const result = await updateHouseholdAPI(
        actualHouseholdId,
        clientToken,
        payload,
      );
      if (result.success) {
        setSuccess(
          result.message || "Account owner information updated successfully!",
        );
        setIsEditing(false);
        if (onDataUpdate) onDataUpdate(editedData);
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(result.error);
        if (result.sessionExpired)
          setTimeout(() => window.location.reload(), 2000);
      }
    } catch {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderField = (label, value, memberId, fieldPath, type = "text") => (
    <div className="py-2">
      <span className="text-sm font-medium text-gray-500">{label}</span>
      {isEditing ? (
        <input
          type={type}
          value={value || ""}
          onChange={(e) =>
            handleInputChange(memberId, fieldPath, e.target.value)
          }
          maxLength={fieldPath?.endsWith("postal_code") ? 5 : undefined}
          className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
            validationErrors[`${memberId}_${fieldPath}`]
              ? "border-red-500"
              : "border-gray-300"
          }`}
        />
      ) : (
        <p className="text-sm text-gray-900 mt-1">{value || "N/A"}</p>
      )}
      {validationErrors[`${memberId}_${fieldPath}`] && (
        <p className="text-red-500 text-xs mt-1">
          {validationErrors[`${memberId}_${fieldPath}`]}
        </p>
      )}
    </div>
  );

  return (
    <ClientPortalLayout
      householdData={householdData}
      currentStep="review-submit"
      title="Final Review"
      subtitle="Review all account owner information before sharing with your advisor."
      headerAction={
        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              navigate(`/household/${actualHouseholdId}/full-review`)
            }
            className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
          >
            Confirm and Submit
          </button>
          <EditControls
            isEditing={isEditing}
            onEdit={handleEdit}
            onSave={handleSubmit}
            onCancel={handleCancel}
            saving={loading}
            disabled={Object.keys(validationErrors).length > 0}
          />
        </div>
      }
    >
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-700 text-sm">{success}</p>
        </div>
      )}

      {householdData?.advisor_note &&
        householdData.advisor_note.trim() !== "" && (
          <div className="mb-4 flex items-start justify-between gap-3 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h8M8 14h6m-2 7H6a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2v8l-4 4z"
                />
              </svg>
              <div>
                <p className="text-xs font-semibold text-emerald-800">
                  Message from your advisor
                </p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                  {householdData.advisor_note}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={confirmClearAdvisorNote}
              disabled={clearingAdvisorNote}
              className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors disabled:opacity-50"
            >
              {clearingAdvisorNote ? "Deleting..." : "Delete"}
            </button>
          </div>
        )}

      {accountOwners.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <p className="text-gray-600 text-lg">No account owners found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {accountOwners.map((ownerData) => {
            const { memberId, member, accounts } = ownerData;
            const currentMember =
              isEditing && editedData
                ? editedData.members?.find((m) => m.id === memberId) || member
                : member;
            const personalInfo = getPersonalInfo(currentMember);
            const address = getAddress(personalInfo);
            const isExpanded = expandedOwners.has(memberId);

            const firstName = personalInfo?.first_name || "N/A";
            const lastName = personalInfo?.last_name || "N/A";
            const email = personalInfo?.email || "N/A";
            const dob = personalInfo?.dob
              ? formatDateForDisplay(personalInfo.dob)
              : "N/A";

            return (
              <div
                key={memberId}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => toggleOwner(memberId)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-emerald-600 font-semibold text-lg">
                        {firstName.charAt(0).toUpperCase()}
                        {lastName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {firstName} {lastName}
                      </h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {accounts.length} account
                        {accounts.length !== 1 ? "s" : ""} · {email}
                      </p>
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {isExpanded && (
                  <div className="px-6 py-5 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                      <div>
                        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                          Personal Information
                        </h4>
                        {renderField(
                          "First Name",
                          personalInfo?.first_name,
                          memberId,
                          "first_name",
                        )}
                        {renderField(
                          "Last Name",
                          personalInfo?.last_name,
                          memberId,
                          "last_name",
                        )}
                        {renderField(
                          "Email",
                          personalInfo?.email,
                          memberId,
                          "email",
                          "email",
                        )}
                        {renderField(
                          "Username",
                          personalInfo?.username,
                          memberId,
                          "username",
                        )}
                        <div className="py-2">
                          <span className="text-sm font-medium text-gray-500">
                            SSN/TIN
                          </span>
                          <p className="text-sm text-gray-900 mt-1">
                            {maskSSN(personalInfo?.ssn_or_tin)}
                          </p>
                        </div>
                        <div className="py-2">
                          <span className="text-sm font-medium text-gray-500">
                            Date of Birth
                          </span>
                          {isEditing ? (
                            <>
                              <input
                                type="date"
                                max={getTodayYmdLocal()}
                                value={
                                  personalInfo?.dob
                                    ? formatDateForInput(personalInfo.dob)
                                    : ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    memberId,
                                    "dob",
                                    e.target.value,
                                  )
                                }
                                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                                  validationErrors[`${memberId}_dob`]
                                    ? "border-red-500"
                                    : "border-gray-300"
                                }`}
                              />
                              {validationErrors[`${memberId}_dob`] && (
                                <p className="text-red-500 text-xs mt-1">
                                  {validationErrors[`${memberId}_dob`]}
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="text-sm text-gray-900 mt-1">{dob}</p>
                          )}
                        </div>
                        <div className="py-2">
                          <span className="text-sm font-medium text-gray-500">
                            Deceased Status
                          </span>
                          {isEditing ? (
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="checkbox"
                                checked={!!personalInfo?.is_deceased}
                                onChange={(e) =>
                                  handleInputChange(
                                    memberId,
                                    "is_deceased",
                                    e.target.checked,
                                  )
                                }
                                className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                              />
                              <span className="text-sm text-gray-700">Deceased</span>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-900 mt-1">
                              {personalInfo?.is_deceased ? "Yes" : "No"}
                            </p>
                          )}
                        </div>

                        {(isEditing ? personalInfo?.is_deceased : personalInfo?.is_deceased) && (
                          <div className="py-2">
                            <span className="text-sm font-medium text-gray-500">
                              Date of Death
                            </span>
                            {isEditing ? (
                              <input
                                type="date"
                                value={
                                  personalInfo?.date_of_death
                                    ? formatDateForInput(personalInfo.date_of_death)
                                    : ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    memberId,
                                    "date_of_death",
                                    e.target.value,
                                  )
                                }
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                              />
                            ) : (
                              <p className="text-sm text-gray-900 mt-1">
                                {formatDateForDisplay(personalInfo?.date_of_death)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                          Address
                        </h4>
                        {address || isEditing ? (
                          <>
                            {renderField(
                              "Address Line 1",
                              address?.address_line_1,
                              memberId,
                              "address.address_line_1",
                            )}
                            {renderField(
                              "Address Line 2",
                              address?.address_line_2,
                              memberId,
                              "address.address_line_2",
                            )}
                            {renderField(
                              "City",
                              address?.city,
                              memberId,
                              "address.city",
                            )}
                            {renderField(
                              "State",
                              address?.province || address?.state,
                              memberId,
                              "address.province",
                            )}
                            {renderField(
                              "Postal Code",
                              address?.postal_code,
                              memberId,
                              "address.postal_code",
                            )}
                            {renderField(
                              "Phone",
                              address?.phone,
                              memberId,
                              "address.phone",
                            )}
                          </>
                        ) : (
                          <p className="text-gray-500 text-sm">
                            No address information available
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </ClientPortalLayout>
  );
};

export default FinalReviewV2;
